/**
 * useDeferredDeviceRemovals
 *
 * Polls the backend deferred removal queue and handles two phases via the
 * Electron hybrid (direct device) path:
 *
 *  PHASE 1 — RESTRICT (runs as soon as a new pending item is detected):
 *    Call SYNC_PERSON on the specific exit device with validUntil = scheduled_at.
 *    This updates the hardware-level time restriction immediately after checkout,
 *    so the device enforces the expiry window even before the face is removed.
 *    No photo is provided → face upload is skipped (only endTime is updated).
 *
 *  PHASE 2 — EXECUTE (runs when scheduled_at has passed):
 *    Remove the face from the specific device via Electron, then call
 *    POST /trigger with local_removal_done=true to confirm on the backend.
 *
 * ARCHITECTURE RULE: the cloud NEVER contacts ControlID/Hikvision devices —
 * this hook (running inside Electron on the customer's LAN) is the ONLY
 * executor of removal pendências. The trigger endpoint is called only after a
 * SUCCESSFUL local removal; on failure the item stays queued and is retried
 * with backoff. Non-Electron browsers skip removals entirely (the pendência
 * waits for the on-site application). 'add' items keep confirming via trigger
 * regardless, since the backend AutoSyncService independently covers grants.
 */

import { useEffect, useRef } from 'react';
import { fetchWithAuth, getApiBaseUrl } from '../services/api';
import { ensureOk } from '../services/httpError';
import { revokePersonPlatesFromCamera } from '../services/lprPlateSync';
import { removePersonFromDevicesVerified, reportCredentialOutcome } from '../utils/deviceRevoke';
import {
  DeviceOperations,
  DeviceInfo,
  CommunicationMode,
} from '../services/deviceCommunication';
import { DeviceIntegration } from '../integrations/devices';
import { isElectron } from '../utils/platform';
import { logger } from '../services/logger';

interface QueueItem {
  id: string;
  person_id: string;
  person_name: string;
  person_photo: string;
  device_id: string;
  device_name: string;
  zone_name: string;
  scheduled_at: string;
  executed_at?: string;
  removal_status: string;
  operation_type?: string; // 'remove' (default) | 'add' (SP window-open sync)
  valid_from?: string; // original role start time — preserved as beginTime on device
}

const POLL_INTERVAL_MS = 30_000;
/**
 * How many queued items one cycle may execute.
 *
 * Each item costs a device fetch, a credential-outcome report and a trigger POST, so an
 * unbounded drain turned a backlog of forty into ~120 requests landing back to back — and
 * repeated it every tick until the backend confirmed them. That burst is what the operational
 * view's Network tab shows, and what the audit log pays for (see the RDS storage incident).
 * The queue is not urgent: the remainder is picked up on the next cycle.
 */
export const MAX_ITEMS_PER_CYCLE = 5;
// Items that keep failing to confirm (trigger never flips removal_status to
// 'confirmed') would otherwise be fully reprocessed — new device fetch, new
// credential-outcome, new trigger — on every single 30s tick forever. After a
// few free retries (covers ordinary transient failures), back off exponentially
// per item instead, capped at MAX_BACKOFF_MS. Never gives up permanently.
const FREE_RETRIES = 3;
const MAX_BACKOFF_MS = 30 * 60_000; // 30 minutes

function nextBackoffMs(attempts: number): number {
  if (attempts <= FREE_RETRIES) return 0;
  return Math.min(POLL_INTERVAL_MS * 2 ** (attempts - FREE_RETRIES), MAX_BACKOFF_MS);
}

async function fetchDeviceInfo(deviceId: string): Promise<DeviceInfo | null> {
  try {
    const resp = await fetchWithAuth(`${getApiBaseUrl()}/api/v1/iot-devices/${deviceId}`);
    if (!resp.ok) return null;
    const d = await resp.json();
    const mfr = (d.manufacturer || '').toLowerCase();
    const vendor: DeviceInfo['vendor'] =
      mfr === 'controlid' ? 'controlid' :
      mfr === 'hikvision' ? 'hikvision' : 'hikvision';
    return {
      id: d.id,
      vendor,
      ip_address: d.ip_address,
      local_ip_address: d.local_ip_address,
      port: d.port,
      username: d.username || 'admin',
      password: d.password || '',
      // Needed to recognise an LPR camera, which is revoked by plate rather than by user
      // record — see the hikvision_lpr branch in the removal loop below.
      device_type: d.device_type,
      config: d.config,
    };
  } catch {
    return null;
  }
}

export function useDeferredDeviceRemovals(customerId: string | null | undefined): void {
  const customerIdRef = useRef(customerId);
  useEffect(() => { customerIdRef.current = customerId; }, [customerId]);

  // Track items already processed for the restrict phase (avoids re-restricting every poll)
  const restrictedRef = useRef<Set<string>>(new Set());
  // Track items currently being executed (avoids concurrent poll cycles handling the same item)
  const inProgressRef = useRef<Set<string>>(new Set());
  // Track execute-phase attempts per item so a stuck item backs off instead of
  // being retried every single poll tick forever — see nextBackoffMs above.
  const attemptTrackerRef = useRef<Map<string, { attempts: number; nextAttemptAt: number }>>(new Map());
  // True while a cycle is walking the queue — see the guard in run().
  const runningRef = useRef(false);

  useEffect(() => {
    if (!customerId) return;

    const run = async () => {
      const cid = customerIdRef.current;
      if (!cid) return;
      // One walk at a time. The immediate-drain event fires on every check-in/check-out whose
      // local removal did not complete, and without this each one started another full pass over
      // the same queue — the passes then ran on top of each other.
      if (runningRef.current) return;
      runningRef.current = true;

      try {
        const resp = await fetchWithAuth(
          `${getApiBaseUrl()}/api/v1/customers/${cid}/deferred-removals/queue`
        );
        if (!resp.ok) return;

        const data = await resp.json();
        const items: QueueItem[] = (data.items || []).filter(
          (i: QueueItem) => i.removal_status !== 'confirmed'
        );
        if (items.length === 0) return;

        // Drop backoff tracking for items no longer in the queue (confirmed elsewhere)
        const itemIds = new Set(items.map(i => i.id));
        Array.from(attemptTrackerRef.current.keys()).forEach(id => {
          if (!itemIds.has(id)) attemptTrackerRef.current.delete(id);
        });

        const electronEnv = isElectron();
        const now = new Date();

        // Bounded slice, in queue order. Items the cycle does not reach stay queued and are
        // taken next time — nothing is dropped, it just stops arriving all at once.
        let budget = MAX_ITEMS_PER_CYCLE;

        for (const item of items) {
          if (budget <= 0) break;
          const scheduledAt = new Date(item.scheduled_at);
          const isReady = scheduledAt <= now;

          if (isReady) {
            // ── PHASE 2: Execute operation ────────────────────────────────────
            const isAdd = item.operation_type === 'add';

            // Removals are executed ONLY by the local (Electron) application.
            // In a plain browser there is nothing to do — leave the pendência
            // for the on-site client instead of asking the cloud to do it.
            if (!isAdd && !electronEnv) continue;

            // Skip if another poll cycle is already handling this item
            if (inProgressRef.current.has(item.id)) continue;
            // Skip if this item is still backing off after repeated failed attempts
            const tracker = attemptTrackerRef.current.get(item.id);
            if (tracker && Date.now() < tracker.nextAttemptAt) continue;
            inProgressRef.current.add(item.id);
            budget--;

            logger.info('useDeferredDeviceRemovals', `Executing ${isAdd ? 'addition' : 'removal'} for item ${item.id}`, {
              personId: item.person_id,
              deviceId: item.device_id,
            });

            let localDone = false;
            let verified = false;
            let alreadyAbsent = false;
            let errorMessage: string | undefined;
            try {
              if (electronEnv) {
                try {
                  const device = await fetchDeviceInfo(item.device_id);
                  if (!device) {
                    errorMessage = 'device info unavailable';
                  }
                  if (device) {
                    if (isAdd) {
                      // SP window opened — push face to device via HYBRID (direct IP first)
                      const parts = item.person_name.trim().split(' ');
                      const firstName = parts.length > 1 ? parts.slice(0, -1).join(' ') : item.person_name;
                      const lastName  = parts.length > 1 ? parts[parts.length - 1] : '';
                      const result = await DeviceIntegration.instance.execute(
                        device,
                        DeviceOperations.SYNC_PERSON,
                        {
                          person_id: item.person_id,
                          first_name: firstName,
                          last_name: lastName,
                          identity_photo_url: item.person_photo || undefined,
                          profile_picture_url: item.person_photo || undefined,
                          isVisitor: true,
                        },
                        CommunicationMode.HYBRID
                      );
                      localDone = result.success;
                      // Report this grant attempt (success or failure) the same way
                      // revokes are reported — previously only the boolean localDone
                      // was ever used, so a failed SP window-open sync left no trace.
                      reportCredentialOutcome(
                        cid, item.person_id, item.device_id,
                        { success: result.success, latencyMs: result.latencyMs, source: result.source, errorMessage: result.error },
                        'grant'
                      );
                    } else if (device.device_type === 'hikvision_lpr') {
                      // A DS-TCG406 has no user database: "remove this person" means removing
                      // their vehicles' plates from its allow-list. Running the face/card
                      // removal below against it fails on every retry while the car keeps
                      // opening the gate.
                      const cameraId = device.config?.hikvision_lpr_camera_id;
                      if (!cameraId) {
                        errorMessage = 'LPR device is not linked to a camera';
                      } else {
                        const outcome = await revokePersonPlatesFromCamera(cid, item.person_id, cameraId);
                        localDone = outcome.allSucceeded;
                        // The plate list is re-read to confirm each removal, so a success here
                        // is already verified against the camera.
                        verified = outcome.allSucceeded;
                        errorMessage = outcome.errorMessage;
                      }
                    } else {
                      // Verified removal: absent pre-check, delete, read-back.
                      const { allSucceeded, outcomes } = await removePersonFromDevicesVerified(
                        cid, [device], item.person_id, item.person_name, 2
                      );
                      localDone = allSucceeded;
                      const outcome = outcomes[0]?.result;
                      verified = outcome?.readbackVerified === true;
                      alreadyAbsent = outcome?.alreadyAbsent === true;
                      errorMessage = outcome?.errorMessage;
                    }
                  }
                } catch (err: any) {
                  errorMessage = err?.message || String(err);
                  logger.warn('useDeferredDeviceRemovals', `Device ${isAdd ? 'addition' : 'removal'} failed`, { id: item.id, err });
                }
              }

              // Report the REAL outcome to the backend. On success the item is
              // confirmed (verified outcomes recorded with the stronger read-back
              // source); on failure the backend records revoke_failed but keeps the
              // pendência queued for this client to retry with backoff — the cloud
              // never executes removals itself.
              if (isAdd || localDone || errorMessage) {
                try {
                  const triggerRes = await fetchWithAuth(
                    `${getApiBaseUrl()}/api/v1/customers/${cid}/deferred-removals/${item.id}/trigger`,
                    {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        local_removal_done: localDone,
                        verified,
                        already_absent: alreadyAbsent,
                        error_message: errorMessage,
                      }),
                    }
                  );
                  // Unchecked, a rejected report was logged as "Reported ... outcome" below,
                  // hiding the fact that the pendência is still queued awaiting a retry.
                  await ensureOk(triggerRes, 'Falha ao reportar o resultado da remoção');
                  logger.info('useDeferredDeviceRemovals', 'Reported deferred removal outcome', {
                    id: item.id,
                    localDone,
                    verified,
                    alreadyAbsent,
                  });
                } catch (err) {
                  logger.warn('useDeferredDeviceRemovals', 'Failed to report outcome', { id: item.id, err });
                }
              } else {
                logger.info('useDeferredDeviceRemovals', 'Local removal not confirmed - item stays queued for retry', {
                  id: item.id,
                });
              }
            } finally {
              inProgressRef.current.delete(item.id);
              // Record this attempt; item disappears from the queue (pruned above)
              // once the backend actually confirms it, so this only grows for
              // genuinely stuck items.
              const prevAttempts = attemptTrackerRef.current.get(item.id)?.attempts || 0;
              const attempts = prevAttempts + 1;
              attemptTrackerRef.current.set(item.id, {
                attempts,
                nextAttemptAt: Date.now() + nextBackoffMs(attempts),
              });
            }

          } else if (electronEnv && !restrictedRef.current.has(item.id)) {
            budget--;
            // ── PHASE 1: Restrict valid_until on device ───────────────────────
            // Updates endTime on the device so hardware enforces the time window
            // before the face is physically removed. No photo provided → upload skipped.
            logger.info('useDeferredDeviceRemovals', `Restricting access until ${item.scheduled_at}`, {
              id: item.id,
              personId: item.person_id,
              deviceId: item.device_id,
            });
            try {
              const device = await fetchDeviceInfo(item.device_id);
              if (device) {
                const parts = item.person_name.trim().split(' ');
                const firstName = parts.length > 1 ? parts.slice(0, -1).join(' ') : item.person_name;
                const lastName  = parts.length > 1 ? parts[parts.length - 1] : '';
                await DeviceIntegration.instance.execute(
                  device,
                  DeviceOperations.SYNC_PERSON,
                  {
                    person_id: item.person_id,
                    first_name: firstName,
                    last_name: lastName,
                    validFrom: item.valid_from,    // preserve original beginTime
                    validUntil: item.scheduled_at, // new endTime = delay window
                    isVisitor: true,
                    // No identity_photo_url / profile_picture_url → face upload skipped
                  },
                  CommunicationMode.HYBRID
                );
                restrictedRef.current.add(item.id);
                logger.info('useDeferredDeviceRemovals', 'Device access window restricted', {
                  id: item.id,
                  until: item.scheduled_at,
                });
              }
            } catch (err) {
              logger.warn('useDeferredDeviceRemovals', 'Failed to restrict device access', {
                id: item.id,
                err,
              });
            }
          }
        }
      } catch (err) {
        logger.debug('useDeferredDeviceRemovals', 'Poll error', { err });
      } finally {
        runningRef.current = false;
      }
    };

    run();
    const id = setInterval(run, POLL_INTERVAL_MS);

    // Immediate drain signal: exit/entry detection dispatches this right after a
    // check-out/check-in whose local removal did not complete, so the freshly
    // queued pendência is executed now instead of waiting up to POLL_INTERVAL_MS.
    const pollNow = () => { run(); };
    window.addEventListener('panther:deferred-removals-poll', pollNow);

    return () => {
      clearInterval(id);
      window.removeEventListener('panther:deferred-removals-poll', pollNow);
    };
  }, [customerId]);
}
